import React, { useState, useRef, useEffect } from 'react';
import Modal from './Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';

const MAX_SIZE = 10 * 1024 * 1024; // 10MB, the server's limit
const PDF_SCALE = 2; // render PDFs at 2x for a crisp plan

// Render the first page of a PDF to a PNG blob. pdf.js is loaded only when a
// PDF is actually chosen, so it stays out of the main bundle.
const renderPdfToPng = async (file) => {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.js', import.meta.url).toString();

        const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
        try {
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: PDF_SCALE });
                const canvas = document.createElement('canvas');
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                const ctx = canvas.getContext('2d');
                // PDFs are transparent by default; give the plan a white page.
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                await page.render({ canvasContext: ctx, viewport }).promise;
                return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        } finally {
                pdf.destroy();
        }
};

const FloorPlanUploadModal = ({ isOpen, onClose, onSave }) => {
        const [blob, setBlob] = useState(null);
        const [previewUrl, setPreviewUrl] = useState('');
        const [isDragging, setIsDragging] = useState(false);
        const [isProcessing, setIsProcessing] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [error, setError] = useState('');
        const fileInputRef = useRef(null);

        useEffect(() => {
                if (!blob) {
                        setPreviewUrl('');
                        return;
                }
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                return () => URL.revokeObjectURL(url);
        }, [blob]);

        const reset = () => {
                setBlob(null);
                setError('');
        };

        const handleClose = () => {
                reset();
                onClose();
        };

        const handleFile = async (file) => {
                setError('');
                if (!file) return;
                const isPdf = file.type === 'application/pdf';
                if (!isPdf && !['image/jpeg', 'image/png'].includes(file.type)) {
                        setError('Please select a PDF, PNG or JPEG file.');
                        return;
                }
                if (!isPdf && file.size > MAX_SIZE) {
                        setError('File is too large. Maximum size is 10MB.');
                        return;
                }
                if (!isPdf) {
                        setBlob(file);
                        return;
                }

                setIsProcessing(true);
                try {
                        const png = await renderPdfToPng(file);
                        if (!png) throw new Error('empty render');
                        if (png.size > MAX_SIZE) {
                                setError('The rendered PDF page is larger than 10MB. Try a simpler PDF or export it as PNG.');
                                return;
                        }
                        setBlob(png);
                } catch (e) {
                        console.error('Failed to render PDF', e);
                        setError('Could not read this PDF. Try exporting the floor plan as PNG instead.');
                } finally {
                        setIsProcessing(false);
                }
        };

        const onDragOver = (e) => {
                e.preventDefault();
                setIsDragging(true);
        };

        const onDragLeave = (e) => {
                e.preventDefault();
                setIsDragging(false);
        };

        const onDrop = (e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                        handleFile(e.dataTransfer.files[0]);
                }
        };

        const handleSave = async () => {
                if (!blob) return;
                setIsSaving(true);
                try {
                        if (await onSave(blob)) {
                                reset();
                                onClose();
                        }
                } finally {
                        setIsSaving(false);
                }
        };

        return (
                <Modal isOpen={isOpen} onClose={handleClose} widthClass="max-w-2xl">
                        <div className="p-6">
                                <h3 className="text-lg font-bold mb-4">Upload floor plan</h3>
                                {!blob ? (
                                        <>
                                                <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="application/pdf,image/png,image/jpeg"
                                                        data-testid="floor-plan-file-input"
                                                        onChange={(e) => handleFile(e.target.files?.[0])}
                                                        className="hidden"
                                                />
                                                <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        onDragOver={onDragOver}
                                                        onDragLeave={onDragLeave}
                                                        onDrop={onDrop}
                                                        disabled={isProcessing}
                                                        className={`w-full p-8 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer ${isDragging ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-brand hover:bg-gray-50'}`}
                                                >
                                                        <FontAwesomeIcon icon={isProcessing ? faSpinner : faUpload} spin={isProcessing} className="text-gray-400 text-3xl mb-3" />
                                                        <p className="text-gray-600 font-medium">{isProcessing ? 'Reading PDF…' : 'Click or drag to upload a floor plan'}</p>
                                                        <p className="text-gray-400 text-sm mt-1">PDF, PNG or JPEG up to 10MB. For PDFs, only the first page is used.</p>
                                                </button>
                                                {error && (
                                                        <p data-testid="floor-plan-upload-error" className="text-red-500 text-sm mt-2">{error}</p>
                                                )}
                                        </>
                                ) : (
                                        <>
                                                {previewUrl && (
                                                        <img src={previewUrl} alt="Floor plan preview" className="w-full max-h-[60vh] object-contain border rounded-lg" />
                                                )}
                                                <div className="mt-6 flex justify-end space-x-4">
                                                        <button
                                                                onClick={handleSave}
                                                                disabled={isSaving}
                                                                data-testid="floor-plan-save"
                                                                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                        >
                                                                {isSaving ? 'Saving…' : 'Save'}
                                                        </button>
                                                        <button
                                                                onClick={reset}
                                                                disabled={isSaving}
                                                                className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                        >
                                                                Choose Another
                                                        </button>
                                                        <button
                                                                onClick={handleClose}
                                                                disabled={isSaving}
                                                                className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                        >
                                                                Cancel
                                                        </button>
                                                </div>
                                        </>
                                )}
                        </div>
                </Modal>
        );
};

export default FloorPlanUploadModal;
